import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Rule } from 'types';
import { CompensationStatus, Namespace } from 'enums';

export const useCompensationFormItems = (rule: Rule | undefined, status?: CompensationStatus) => {
  const { t } = useTranslation(Namespace.CompensationForm);

  const isAttachmentRequired = useMemo(() => {
    return (!status || status === CompensationStatus.UpdateRequired) && rule?.requiresBill;
  }, [status, rule]);

  return useMemo(() => {
    return {
      employeeFormItemProps: {
        name: 'employeeID',
        label: t('employee'),
        rules: [{ type: 'number' as const, required: true, message: t('employeeError') }],
      },
      typeFormItemProps: {
        name: 'type',
        label: t('compensationType'),
        rules: [{ type: 'string' as const, required: true, message: t('compensationTypeError') }],
      },
      amountFormItemProps: {
        name: 'amount',
        label: !rule || rule.requiresBill ? t('billAmount') : t('paymentAmount'),
        rules: [{ type: 'number' as const, required: true, message: t('billAmountError') }],
      },
      currencyFormItemProps: {
        name: 'currency',
        label: t('currency'),
        rules: [{ type: 'string' as const, required: true, message: t('currencyError') }],
      },
      billedOnFormItemProps: {
        name: 'billedOn',
        label: t('billedOn'),
        rules: [{ type: 'date' as const, required: rule?.requiresBill, message: t('billedOnError') }],
      },
      targetPeriodFormItemProps: {
        name: 'targetPeriod',
        label: t('targetPeriod'),
        rules: [{ type: 'string' as const, required: true, message: t('targetPeriodError') }],
      },
      targetPeriodEditorFormItemProps: {
        name: 'targetPeriods',
        label: t('targetPeriod'),
        rules: [{ type: 'array' as const, required: true, message: t('targetPeriodError') }],
      },
      employeeCommentFormItemProps: {
        name: 'employeeComment',
        label: t('employeeComment'),
      },
      reviewerCommentFormItemProps: {
        name: 'reviewerComment',
        label: t('reviewerComment'),
      },
      statusFormItemProps: {
        name: 'status',
        label: t('status'),
      },
      attachmentsCounterFormItemProps: {
        name: 'attachmentsCounter',
        rules: [{ type: 'number' as const, min: isAttachmentRequired ? 1 : 0, message: t('attachmentsError') }],
      },
    };
  }, [t, rule, isAttachmentRequired]);
};
